import { useState, useEffect } from "react";
import { AlertTriangle, Clock, CheckCircle2, X, FileSearch, Shield, FileText } from "lucide-react";
import { Button } from "./ui/button";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function ComplianceBanner() {
  const { token } = useAuth();
  const [compliance, setCompliance] = useState(null);
  const [dismissed, setDismissed] = useState(false);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    if (!token) { setLoading(false); return; }
    const controller = new AbortController();
    const fetchComplianceStatus = async () => {
      try {
        const response = await fetch(`${API_URL}/api/compliance/status`, {
          headers: { Authorization: `Bearer ${token}` },
          signal: controller.signal,
        });
        if (response.ok) {
          const data = await response.json();
          setCompliance(data);
        }
      } catch (error) {
        // The banner is a non-critical, optional overlay. Aborted requests on
        // navigation/unmount and transient network cancellations (reported by the
        // browser as TypeError "Failed to fetch", with the AbortError name dropped
        // by PostHog's fetch wrapper) are expected — log only genuine errors.
        const expected = error.name === "AbortError" || error.message === "Failed to fetch";
        if (!expected) {
          console.error("Failed to fetch compliance status:", error);
        }
      } finally {
        setLoading(false);
      }
    };
    fetchComplianceStatus();
    return () => controller.abort();
  }, [token]);

  if (loading || !compliance || !compliance.show_warning_banner || dismissed) {
    return null;
  }

  const getUrgencyColor = () => {
    // Check if any item is overdue
    if (
      (compliance.bia_days_until_due !== null && compliance.bia_days_until_due <= 0) ||
      (compliance.risk_days_until_due !== null && compliance.risk_days_until_due <= 0) ||
      (compliance.plan_review_days_until_due !== null && compliance.plan_review_days_until_due <= 0)
    ) {
      return "bg-red-600";
    }
    // Check if any item is due within 30 days
    if (
      (compliance.bia_days_until_due !== null && compliance.bia_days_until_due <= 30) ||
      (compliance.risk_days_until_due !== null && compliance.risk_days_until_due <= 30) ||
      (compliance.plan_review_days_until_due !== null && compliance.plan_review_days_until_due <= 30)
    ) {
      return "bg-sky-700";
    }
    return "bg-amber-500";
  };

  const getItems = () => {
    const items = [];
    
    if (compliance.bia_days_until_due !== null && compliance.bia_days_until_due <= 90) {
      items.push({
        type: "BIA",
        days: compliance.bia_days_until_due,
        icon: FileSearch,
        action: () => navigate("/bia"),
        actionText: "Update BIA"
      });
    }
    
    if (compliance.risk_days_until_due !== null && compliance.risk_days_until_due <= 90) {
      items.push({
        type: "Risk Assessment",
        days: compliance.risk_days_until_due,
        icon: Shield,
        action: () => navigate("/risks"),
        actionText: "Update Risk"
      });
    }
    
    if (compliance.plan_review_days_until_due !== null && compliance.plan_review_days_until_due <= 90) {
      items.push({
        type: "Plan Review",
        days: compliance.plan_review_days_until_due,
        icon: FileText,
        action: () => navigate("/plans"),
        actionText: "Review Plans"
      });
    }
    
    return items;
  };

  const items = getItems();

  return (
    <div className={`${getUrgencyColor()} text-white px-4 py-3`} data-testid="compliance-banner">
      <div className="max-w-7xl mx-auto">
        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3">
          <div className="flex items-start gap-3">
            <AlertTriangle className="w-5 h-5 flex-shrink-0 mt-0.5" />
            <div>
              <p className="font-semibold text-sm sm:text-base">Annual Compliance Review Required</p>
              <div className="flex flex-wrap gap-x-4 gap-y-1 mt-1">
                {items.map((item, index) => (
                  <div key={index} className="flex items-center gap-1.5 text-xs sm:text-sm opacity-90">
                    <item.icon className="w-3.5 h-3.5" />
                    <span>
                      {item.type}: {item.days <= 0 ? (
                        <span className="font-bold">OVERDUE by {Math.abs(item.days)} days</span>
                      ) : (
                        <span>Due in {item.days} days</span>
                      )}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          </div>
          
          <div className="flex items-center gap-2 w-full sm:w-auto">
            <div className="flex gap-2 flex-1 sm:flex-none">
              {items.slice(0, 2).map((item, index) => (
                <Button
                  key={index}
                  size="sm"
                  variant="secondary"
                  className="text-xs bg-white/20 hover:bg-white/30 border-0 flex-1 sm:flex-none"
                  onClick={item.action}
                >
                  {item.actionText}
                </Button>
              ))}
            </div>
            <button
              onClick={() => setDismissed(true)}
              className="p-1 hover:bg-white/20 rounded transition-colors"
              aria-label="Dismiss banner"
            >
              <X className="w-4 h-4" />
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
